/* DJ r11 — small behaviours for the r11 CRO build. Loaded once from layout/theme.liquid (defer). */

/* DJ r11: product #10 — the Judge.me star badge under the H1 (templates/product.json block
   dj_review_badge) scrolls to the review widget on click, Enter or Space. Delegated, so it keeps
   working if Judge.me re-renders the badge's inner markup. */
(() => {
  const TARGET_ID = 'judgeme_product_reviews';

  /** @param {Event} event */
  function scrollToReviews(event) {
    const badge = event.target instanceof Element ? event.target.closest('[data-dj-review-badge]') : null;
    if (!badge) return;
    if (event.type === 'keydown') {
      const key = /** @type {KeyboardEvent} */ (event).key;
      if (key !== 'Enter' && key !== ' ') return;
    }
    const target = document.getElementById(TARGET_ID);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  document.addEventListener('click', scrollToReviews);
  document.addEventListener('keydown', scrollToReviews);
})();

/* DJ r11: product #21 (row 26) — the Shopify Inbox launcher (fixed, bottom-right) covered the L / XL size buttons on
   first paint on phones. Mark <html data-dj-buybox> while the colour/size picker or the Add to cart row is on screen;
   assets/dj-r11.css hides the launcher host under 750px for exactly that time. No <variant-picker> (not a PDP) → no-op. */
(() => {
  const targets = [
    document.querySelector('.product-details variant-picker'),
    document.querySelector('.product-details product-form-component'),
  ].filter(Boolean);
  if (!targets.length || !('IntersectionObserver' in window)) return;

  const visible = new Set();
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) visible.add(entry.target);
      else visible.delete(entry.target);
    }
    document.documentElement.toggleAttribute('data-dj-buybox', visible.size > 0);
  });
  for (const target of targets) observer.observe(target);
})();

/* DJ r11 (WP3): cart #10 — "Estimated delivery: <date> – <date>" (WP-oct2j dropped the parenthetical) on
   every [data-dj-delivery] line (drawer + /cart). Today + 4 and + 10 business days, Sat/Sun skipped.
   Holiday buffer (Anderson 2026-10-02): from Nov 15 through Dec 31 (inclusive, store time America/Denver) both ends get
   +3 business days; the dates just shift, no extra text. Section Rendering morphs put the server fallback text back,
   so a MutationObserver re-fills it. */
(() => {
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

  /** DJ r11: today's calendar date in store time (America/Denver), as a local noon Date (DST-safe weekday maths). */
  function storeToday() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Denver',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date());
    const get = (/** @type {string} */ type) => Number(parts.find((p) => p.type === type)?.value);
    return new Date(get('year'), get('month') - 1, get('day'), 12);
  }

  /** DJ r11: holiday buffer — Nov 15 to Dec 31 inclusive (store time) adds 3 business days to both ends. */
  function holidayBuffer(/** @type {Date} */ today) {
    const m = today.getMonth() + 1;
    return m === 12 || (m === 11 && today.getDate() >= 15) ? 3 : 0;
  }

  /** @param {number} days */
  function addBusinessDays(days) {
    const d = storeToday();
    let left = days + holidayBuffer(d);
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) left -= 1;
    }
    return d;
  }

  function fill() {
    for (const el of document.querySelectorAll('[data-dj-delivery]')) {
      const min = Number(el.getAttribute('data-dj-delivery-min') || 4);
      const max = Number(el.getAttribute('data-dj-delivery-max') || 10);
      /* DJ r11 (WP-oct2j, owner 2026-10-02): the line is just the two dates; the "4–10 / 7–13 business days" explanation
         moved into the guarantee dialog (snippets/dj-cart-extras.liquid). The dates still include the holiday buffer
         (+3 business days on both ends Nov 15 – Dec 31, store time). */
      const text = `Estimated delivery: ${fmt.format(addBusinessDays(min))} – ${fmt.format(addBusinessDays(max))}`;
      if (el.textContent !== text) el.textContent = text;
    }
  }

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      fill();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fill);
  else fill();
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
})();

/* DJ r11 (WP3): cart #13 — the guarantee strip ([data-dj-dialog-open="<id>"]) opens a small native <dialog> with the
   refund-policy essentials instead of leaving the cart. The dialog lives inside the cart drawer's panel, so the
   drawer's backdrop-click guard sees it as a nested dialog; Escape is handled on the dialog itself (preventDefault)
   so it closes only the dialog, not the drawer (theme-drawer.js skips defaultPrevented Escapes). */
(() => {
  /** @param {KeyboardEvent} event */
  function onKey(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    /** @type {HTMLDialogElement} */ (event.currentTarget).close();
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-dj-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-dj-dialog-open') || '');
      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        event.preventDefault();
        if (!dialog.dataset.djKeyBound) {
          dialog.addEventListener('keydown', onKey);
          dialog.dataset.djKeyBound = '1';
        }
        dialog.showModal();
      }
      return;
    }

    const dialog = target.closest('dialog[data-dj-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    // Close button, or a click on the backdrop (the event target is the <dialog> itself).
    if (target.closest('[data-dj-dialog-close]') || target === dialog) dialog.close();
  });
})();

/* DJ r11 (WP4, General row 46): footer "Back to top". On desktop the page scrolls inside .page-wrapper, so
   scroll that AND the window; then move keyboard focus to the top of the page (the header logo link).
   Without JS the link still jumps to #header-group. */
(() => {
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-dj-back-to-top]') : null;
    if (!link) return;
    event.preventDefault();
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = smooth ? 'smooth' : 'auto';
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollTop > 0) wrapper.scrollTo({ top: 0, behavior });
    window.scrollTo({ top: 0, behavior });
    const first = document.querySelector('#header-group a[href], #header-group button');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  });
})();

/* DJ r11 (WP4, General row 25): the visible header search field (snippets/search.liquid, >= 1200px) hands the
   first keystroke to the theme's own predictive-search modal (#search-modal), so suggestions work as they do from
   the search icon. The field is a plain GET /search form, so Enter still searches if the modal is unavailable. */
(() => {
  document.addEventListener('input', (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || !field.matches('[data-dj-header-search]')) return;
    const modal = /** @type {any} */ (document.getElementById('search-modal'));
    const modalInput = modal ? modal.querySelector('input[type="search"]') : null;
    if (!modal || typeof modal.showDialog !== 'function' || !(modalInput instanceof HTMLInputElement)) return;
    const text = field.value;
    field.value = '';
    modal.showDialog();
    modalInput.value = text;
    modalInput.focus();
    modalInput.setSelectionRange(text.length, text.length);
    // DJ r11 (wpfix): predictive-search.js ignores input events without an `inputType` (`if (!event.inputType) return;`),
    // so a plain Event left the modal on its empty state until another key was pressed. Send a real InputEvent, and
    // send it again once the (lazily loaded) predictive-search component exists in case its listener was not attached yet.
    const fire = () =>
      modalInput.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
    fire();
    if (!customElements.get('predictive-search-component')) {
      customElements.whenDefined('predictive-search-component').then(() => requestAnimationFrame(fire));
    }
  });
})();

/* DJ r11 (WP5, Home 16): "Recently viewed" home row. The theme already records product ids in localStorage
   ("viewedProducts", max 4, written on each PDP by snippets/scripts.liquid). With no history nothing happens: the
   section shell stays `hidden` and nothing is fetched. With history, the cards are requested once (when the row is
   near the viewport) from sections/dj-recently-viewed.liquid via the Section Rendering API — same pattern the
   theme's own predictive-search modal uses — and the shell is revealed only if cards came back. */
(() => {
  const init = () => {
    const shell = document.querySelector('[data-dj-recent]');
    if (!(shell instanceof HTMLElement)) return;

    let ids = [];
    try {
      const parsed = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
      if (Array.isArray(parsed)) ids = parsed.map(String).filter((id) => /^\d+$/.test(id));
    } catch (e) {
      ids = [];
    }
    if (ids.length === 0) return;

    const body = shell.querySelector('[data-dj-recent-body]');
    if (!body) return;

    let started = false;
    const load = async () => {
      if (started) return;
      started = true;
      try {
        const url = new URL('/search', location.origin);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('section_id', 'dj-recently-viewed');
        const response = await fetch(url.toString());
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const list = doc.querySelector('[data-dj-recent-list]');
        if (!list || list.querySelectorAll('.dj-recent__item').length === 0) return;
        body.replaceChildren(list);
        shell.hidden = false;
      } catch (e) {
        /* stay hidden */
      }
    };

    if ('IntersectionObserver' in window) {
      // The shell is display:none while hidden, so observe its parent section wrapper (zero-height is fine).
      const target = shell.parentElement || shell;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(target);
    } else {
      load();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* DJ r11: WP6 (collection #13/#16) — the product page opens on the colour the card showed.
   Card links carry `?color=<colour>` (snippets/dj-card-url.liquid) — a colour only, never a size or a
   variant id. Here that colour is chosen in the main variant picker exactly as a shopper's colour click
   would (assets/variant-picker.js keeps the size gate shut and resolves price/gallery for that colour).
   The param is dropped from the address bar first, so a later reload or share does not re-apply it. */
(() => {
  const params = new URLSearchParams(location.search);
  const wanted = (params.get('color') || '').trim().toLowerCase();
  if (!wanted) return;

  const run = async () => {
    const picker =
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector('variant-picker');
    try {
      const url = new URL(location.href);
      url.searchParams.delete('color');
      history.replaceState(history.state, '', url.toString());
    } catch (e) {
      /* keep going */
    }
    if (!picker || params.has('variant')) return;
    if (window.customElements) await customElements.whenDefined('variant-picker');
    const inputs = Array.from(
      picker.querySelectorAll('fieldset:not([data-bmp-size-fieldset]) input[type="radio"]')
    );
    const input = inputs.find((radio) => radio instanceof HTMLInputElement && radio.value.trim().toLowerCase() === wanted);
    if (!(input instanceof HTMLInputElement) || input.checked || input.disabled) return;
    input.click();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* DJ r11: WP6 (collection #16/#13) — colour dots on collection cards (snippets/dj-card-colors.liquid).
   Hover (mouse) previews a colour; tap/click chooses it. Both swap the card image to that colour's slide
   the card already carries (hidden + lazy, so it is fetched only when shown) and set the card links'
   `color=` param. Colour only: no size, no variant id, no request. */
(() => {
  const LINKS = 'a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]';

  /** @param {Element} card @param {HTMLElement} dot */
  function show(card, dot) {
    const mediaId = dot.dataset.djMedia;
    const slides = Array.from(card.querySelectorAll('[ref="cardGallery"] slideshow-slide'));
    const target = mediaId ? slides.find((slide) => slide.getAttribute('slide-id') === mediaId) : null;
    if (target) {
      for (const slide of slides) {
        const visible = slide === target;
        slide.hidden = !visible;
        slide.setAttribute('aria-hidden', String(!visible));
      }
    }
    const color = dot.dataset.djColor || '';
    for (const link of card.querySelectorAll(LINKS)) {
      if (!(link instanceof HTMLAnchorElement) || !link.getAttribute('href')) continue;
      const url = new URL(link.href);
      url.searchParams.delete('variant');
      if (color) url.searchParams.set('color', color);
      link.href = url.pathname + url.search + url.hash;
    }
  }

  /** @param {Element} card */
  function chosenDot(card) {
    return card.querySelector('.dj-card-colors__dot[aria-pressed="true"]');
  }

  // The card title link is rendered by blocks/product-title.liquid with the plain product.url (which can carry a
  // filter-chosen variant= id). Before it is followed, give it the card's own link (colour only, no variant).
  /** @param {Event} event */
  const syncTitleLink = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[ref="productTitleLink"]') : null;
    const card = link?.closest('product-card');
    const main = card?.querySelector('a[ref="productCardLink"]');
    if (link instanceof HTMLAnchorElement && main instanceof HTMLAnchorElement && main.getAttribute('href')) {
      link.setAttribute('href', main.getAttribute('href') || '');
    }
  };
  for (const type of ['pointerdown', 'focusin', 'click', 'auxclick', 'contextmenu']) {
    document.addEventListener(type, syncTitleLink, true);
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const dot = event.target.closest('.dj-card-colors__dot');
    const card = dot?.closest('product-card');
    if (dot instanceof HTMLElement && card) show(card, dot);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const group = event.target.closest('[data-dj-card-colors]');
    if (!group) return;
    if (event.relatedTarget instanceof Node && group.contains(event.relatedTarget)) return;
    const card = group.closest('product-card');
    const dot = card ? chosenDot(card) : null;
    if (card && dot instanceof HTMLElement) show(card, dot);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const dot = event.target.closest('.dj-card-colors__dot');
    const card = dot?.closest('product-card');
    if (!(dot instanceof HTMLElement) || !card) return;
    event.preventDefault();
    for (const other of card.querySelectorAll('.dj-card-colors__dot')) {
      other.setAttribute('aria-pressed', String(other === dot));
    }
    show(card, dot);
  });
})();

/* DJ r11: WP6 (collection #2/#21) — the mobile "Sort" button's native <select> (blocks/filters.liquid) hands
   its value to the filter drawer's own sort select, whose change handler re-renders the results with the
   active filters kept. */
document.addEventListener('change', (event) => {
  const select = event.target instanceof Element ? event.target.closest('[data-dj-mobile-sort]') : null;
  if (!(select instanceof HTMLSelectElement)) return;
  const drawerSelect = document.querySelector('#filters-drawer select[name="sort_by"]');
  if (drawerSelect instanceof HTMLSelectElement && !drawerSelect.disabled) {
    drawerSelect.value = select.value;
    drawerSelect.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }
  const url = new URL(location.href);
  url.searchParams.set('sort_by', select.value);
  url.searchParams.delete('page');
  location.href = url.toString();
});

/* DJ r11: WP6 (brief Phase E, Anderson 2026-09-29) — "See more" on collection pages (snippets/dj-see-more.liquid).
   The link is a real ?page=N link (crawlable, works without JS); with JS a click appends the next page's
   cards in place. Never on scroll. Loaded pages are kept in history.state (mirrored in sessionStorage);
   when the shopper comes Back to this entry the same pages are fetched again and the scroll position is
   restored, so they land on the same products. */
(() => {
  const STATE_KEY = 'djSeeMore';
  const STORE_PREFIX = 'dj-see-more:';
  const SQUEEZE = window.matchMedia('(min-width: 990px)');

  const getBlock = () => document.querySelector('[data-dj-see-more]');
  const getGrid = () => document.querySelector('results-list [ref="grid"]');
  const scroller = () =>
    (SQUEEZE.matches && document.querySelector('.page-wrapper')) || document.scrollingElement || document.documentElement;

  function listKey() {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }

  function currentState() {
    return history.state && typeof history.state === 'object' ? history.state : {};
  }

  /** @param {number | null} scroll */
  function save(scroll) {
    const block = getBlock();
    if (!(block instanceof HTMLElement)) return;
    const entry = {
      key: listKey(),
      start: Number(block.dataset.startPage),
      last: Number(block.dataset.lastPage),
      scroll,
    };
    try {
      history.replaceState({ ...currentState(), [STATE_KEY]: entry }, '');
    } catch (e) {
      /* state too large — sessionStorage below still has it */
    }
    try {
      sessionStorage.setItem(STORE_PREFIX + entry.key, JSON.stringify(entry));
    } catch (e) {
      /* private mode */
    }
  }

  /** @param {HTMLElement} block @param {number} page */
  async function fetchPage(block, page) {
    const url = new URL(location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', block.dataset.sectionId || '');
    url.hash = '';
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`See more: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /** @param {HTMLElement} block @param {Document} doc @returns {Element[]} the appended cards */
  function append(block, doc) {
    const grid = getGrid();
    if (!grid) return [];
    const items = Array.from(doc.querySelectorAll('[ref="grid"] > [ref="cards[]"]'));
    grid.append(...items);
    const next = doc.querySelector('[data-dj-see-more]');
    const link = block.querySelector('[data-dj-see-more-link]');
    if (next instanceof HTMLElement) {
      block.dataset.lastPage = next.dataset.startPage || block.dataset.lastPage;
      const to = next.querySelector('[data-dj-see-more-to]');
      const mine = block.querySelector('[data-dj-see-more-to]');
      if (to && mine) mine.textContent = to.textContent;
      const nextLink = next.querySelector('[data-dj-see-more-link]');
      if (link instanceof HTMLAnchorElement) {
        if (nextLink instanceof HTMLAnchorElement) {
          const href = new URL(nextLink.href);
          href.searchParams.delete('section_id');
          link.href = href.pathname + href.search;
        } else {
          link.remove();
        }
      }
    }
    return items;
  }

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-dj-see-more-link]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const mouse = /** @type {MouseEvent} */ (event);
    if (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.altKey || mouse.button > 0) return;
    const block = link.closest('[data-dj-see-more]');
    if (!(block instanceof HTMLElement)) return;
    event.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');
    const label = link.textContent;
    link.textContent = 'Loading…';
    try {
      const doc = await fetchPage(block, Number(block.dataset.lastPage) + 1);
      const added = append(block, doc);
      save(null);
      const first = added[0]?.querySelector('a[href]');
      if (first instanceof HTMLElement) first.focus({ preventScroll: true });
    } catch (e) {
      location.href = link.href;
      return;
    } finally {
      link.removeAttribute('aria-busy');
      link.textContent = label;
    }
  });

  // Leaving the page (e.g. opening a product): remember how far the list was loaded and where the shopper was.
  window.addEventListener('pagehide', () => {
    const block = getBlock();
    if (!(block instanceof HTMLElement)) return;
    if (Number(block.dataset.lastPage) <= Number(block.dataset.startPage)) return;
    save(scroller().scrollTop);
    try {
      history.scrollRestoration = 'manual';
    } catch (e) {
      /* ignore */
    }
  });

  const restore = async () => {
    const block = getBlock();
    if (!(block instanceof HTMLElement)) return;
    let entry = currentState()[STATE_KEY];
    if (!entry) {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && /** @type {PerformanceNavigationTiming} */ (nav).type === 'back_forward') {
        try {
          entry = JSON.parse(sessionStorage.getItem(STORE_PREFIX + listKey()) || 'null');
        } catch (e) {
          entry = null;
        }
      }
    }
    if (!entry || entry.key !== listKey()) return;
    const start = Number(block.dataset.startPage);
    const pages = Number(block.dataset.pages);
    const last = Math.min(Number(entry.last), pages);
    if (entry.start !== start || !(last > start) || Number(block.dataset.lastPage) >= last) return;

    block.setAttribute('aria-busy', 'true');
    try {
      const docs = await Promise.all(
        Array.from({ length: last - start }, (_, i) => fetchPage(block, start + 1 + i))
      );
      for (const doc of docs) append(block, doc);
    } catch (e) {
      /* partial restore is fine — the See more link still points at the next page */
    } finally {
      block.removeAttribute('aria-busy');
    }
    const top = Number.isFinite(entry.scroll) ? entry.scroll : currentState().scrollTop;
    if (Number.isFinite(top)) {
      requestAnimationFrame(() => scroller().scrollTo({ top, behavior: 'instant' }));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restore);
  else restore();
})();

/* DJ r11 (WP-oct4a): 5 — desktop header search (>= 1280px). Focusing the header field opens the theme's own
   predictive-search panel (#search-modal) straight away, so its empty state ("Try:" chips + recently viewed) shows
   on focus the way it does on phones, and the panel is placed at the field (CSS: .dj-search-anchored in
   dj-r11.css) instead of the page's left edge. Typing, results, Escape and click-outside are the panel's own
   (dialog.js / predictive-search.js are not changed). When the panel closes, the browser returns focus to the
   field; that refocus is ignored so the panel does not reopen. Phones/tablets: the field is not shown, nothing runs. */
(() => {
  const desktop = window.matchMedia('(min-width: 1280px)');
  const PANEL_WIDTH = 640;
  let anchorField = null;
  let closedAt = 0;

  const getModal = () => /** @type {any} */ (document.getElementById('search-modal'));

  const place = (modal, field) => {
    const rect = field.getBoundingClientRect();
    if (!rect.width) return;
    const viewport = document.documentElement.clientWidth;
    const width = Math.min(PANEL_WIDTH, viewport - 32);
    const left = Math.max(16, Math.min(rect.right - width, viewport - 16 - width));
    const header = modal.querySelector('.predictive-search-form__header');
    const headerHeight = header instanceof HTMLElement && header.offsetHeight ? header.offsetHeight : 50;
    const top = Math.max(8, Math.round(rect.top + rect.height / 2 - headerHeight / 2));
    modal.style.setProperty('--dj-anchor-top', `${top}px`);
    modal.style.setProperty('--dj-anchor-left', `${Math.round(left)}px`);
    modal.style.setProperty('--dj-anchor-width', `${Math.round(width)}px`);
  };

  const headerField = (target) =>
    target instanceof HTMLInputElement && target.matches('[data-dj-header-search]') ? target : null;

  // Returns the modal when it may open anchored to the field now, else null.
  const prepare = (field) => {
    if (!desktop.matches) return null;
    const modal = getModal();
    const dialog = modal ? modal.querySelector('dialog') : null;
    if (!modal || typeof modal.showDialog !== 'function' || !dialog) return null;
    // Focus coming back from the closing panel (or a panel already open): do nothing.
    if (dialog.open || dialog.classList.contains('dialog-closing') || Date.now() - closedAt < 400) return null;
    // Already opening from this field (focus and click arrive together): open once.
    if (modal.classList.contains('dj-search-anchored')) return null;
    bind(modal);
    anchorField = field;
    place(modal, field);
    modal.classList.add('dj-search-anchored');
    return modal;
  };

  const open = (event) => {
    const field = headerField(event.target);
    if (!field || field.value) return;
    const modal = prepare(field);
    if (modal) modal.showDialog();
  };

  // Focus (click or Tab) opens the panel; a click on the field that kept focus after Escape opens it again.
  document.addEventListener('focusin', open);
  document.addEventListener('click', open);

  // Typing in the field (it keeps focus after Escape) still goes through the WP4 hand-off below/above, which opens
  // the panel itself; this capture listener runs first and only anchors it.
  document.addEventListener(
    'input',
    (event) => {
      const field = headerField(event.target);
      if (field) prepare(field);
    },
    true
  );

  // dialog.js dispatches dialog:open / dialog:close on the component without bubbling, so listen on it directly.
  const bind = (modal) => {
    if (modal.dataset.djAnchorBound) return;
    modal.dataset.djAnchorBound = 'true';
    modal.addEventListener('dialog:open', () => {
      if (!anchorField || !modal.classList.contains('dj-search-anchored')) return;
      requestAnimationFrame(() => place(modal, anchorField));
    });
    const reset = () => {
      closedAt = Date.now();
      modal.classList.remove('dj-search-anchored');
      anchorField = null;
    };
    modal.addEventListener('dialog:close', reset);
    // Safety net: the native close event also clears the anchored state if the dialog is closed another way.
    const dialog = modal.querySelector('dialog');
    if (dialog) dialog.addEventListener('close', reset);
  };

  window.addEventListener('resize', () => {
    const modal = getModal();
    if (modal && anchorField && modal.classList.contains('dj-search-anchored')) place(modal, anchorField);
  });
})();

/* DJ r11 (WP-oct4a): 6 — cart / chat drawers push the page (stock base.css .page-wrapper--drawer-open = margin-right
   of the drawer width). With the page ~960px wide the header field (shown by a >= 1280px viewport media query)
   left the menu no room, so the header switched to the phone layout and stayed there after the drawer closed
   (live r10 has no field and keeps its menu). Hide the field while the page is pushed and until the page has
   slid back (margin-right transition), then show it again; dj-r11.css (.dj-hs-off) shows the stock icon meanwhile. */
(() => {
  const init = () => {
    const wrapper = document.querySelector('.page-wrapper');
    const header = document.getElementById('header-component');
    if (!wrapper || !header) return;
    let timer = 0;
    const restore = () => {
      clearTimeout(timer);
      if (wrapper.classList.contains('page-wrapper--drawer-open') || !header.classList.contains('dj-hs-off')) return;
      header.classList.remove('dj-hs-off');
      // The menu's overflow-list only re-measures when its own size changes; once items sit in "More" its size can
      // stay put, so items stayed hidden after the drawer closed (seen at 1280). Ask it to re-measure, using the
      // 'reflow' event overflow-list.js itself listens for.
      const list = header.querySelector('overflow-list');
      if (list) list.dispatchEvent(new CustomEvent('reflow', { detail: { lastVisibleElement: null } }));
    };
    new MutationObserver(() => {
      if (wrapper.classList.contains('page-wrapper--drawer-open')) {
        clearTimeout(timer);
        header.classList.add('dj-hs-off');
      } else if (header.classList.contains('dj-hs-off')) {
        clearTimeout(timer);
        timer = window.setTimeout(restore, 700);
      }
    }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    wrapper.addEventListener('transitionend', (event) => {
      if (event.target === wrapper && event.propertyName === 'margin-right') restore();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
